<template>
  <div>
    <div
      v-if="cameraIsStarted && !isMobile"
      class="custom-modal-overlay"
    >
      <div class="custom-modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            Reconhecimento Facial
          </h5>
          <button
            type="button"
            class="close"
            aria-label="Close"
            @click="closeCamera"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <face-detection-overlay
            :is-detecting="isDetecting"
            :face-detected="faceDetected"
            :face-inside-oval="faceInsideOval"
            :is-mobile="isMobile"
          />

          <div class="camera-container">
            <div class="camera-wrapper">
              <v-easy-camera
                v-if="cameraIsStarted"
                id="video"
                ref="easyCamera"
                v-model="picture"
                :width="640"
                :height="480"
                :autoplay="false"
                class="mirrored-camera"
                @approve="handlePicture"
              />
              <canvas
                id="overlay"
                width="640"
                height="480"
                class="position-absolute overlay-canvas"
                style="top: 0; left: 0"
              />
              <face-guide-overlay
                :is-mobile="false"
                :face-detected="faceDetected && faceInsideOval"
              />

              <div
                v-if="debugInfo"
                class="debug-info"
              >
                <div>Face detected: {{ faceDetected }}</div>
                <div>Inside oval: {{ faceInsideOval }}</div>
                <div>Face center: {{ debugFaceCenter }}</div>
                <div>Oval center: {{ debugOvalCenter }}</div>
              </div>

              <countdown-timer
                v-if="showCountdown"
                :countdown="captureCountdown"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeCamera"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="isCapturing || !(faceDetected && faceInsideOval)"
            @click="captureAndSend"
          >
            {{ isCapturing ? 'Processando...' : 'Marcar Ponto' }}
          </button>
        </div>
      </div>
    </div>

    <mobile-camera
      v-if="cameraIsStarted && isMobile"
      ref="mobileCamera"
      :is-detecting="isDetecting"
      :face-detected="faceDetected"
      :face-inside-oval="faceInsideOval"
      :is-capturing="isCapturing"
      :picture="picture"
      @capture="captureAndSend"
      @close="closeCamera"
      @picture-ready="handlePicture"
    />
  </div>
</template>

<script>
import EasyCamera from 'easy-vue-camera'
import * as faceapi from 'face-api.js'
import FaceDetectionOverlay from './FaceDetectionOverlay.vue'
import FaceGuideOverlay from './FaceGuideOverlay.vue'
import CountdownTimer from './CountdownTimer.vue'
import MobileCamera from './MobileCamera.vue'

export default {
  name: 'FaceRecognitionCamera',
  components: {
    'v-easy-camera': EasyCamera,
    FaceDetectionOverlay,
    FaceGuideOverlay,
    CountdownTimer,
    MobileCamera,
  },
  props: {
    location: {
      type: Object,
      required: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cameraIsStarted: false,
      isLoadingModels: true,
      isDetecting: false,
      isCapturing: false,
      picture: '',
      detectionInterval: null,
      faceDetected: false,
      faceInsideOval: false,
      debugInfo: false,
      debugFaceCenter: '',
      debugOvalCenter: '',
      autoCapture: true,
      faceInsideCount: 0,
      minFaceDetectionTime: 5,
      captureCountdown: 0,
      showCountdown: false,
    }
  },
  async mounted() {
    await this.loadFaceApiModels()
  },
  beforeDestroy() {
    this.closeCamera()
  },
  methods: {
    async startCamera() {
      this.cameraIsStarted = true
      this.$nextTick(() => {
        setTimeout(() => this.startFaceDetection(), 1000)
      })
    },

    isFaceInsideOval(faceBox, canvasWidth, canvasHeight) {
      if (!faceBox) return false

      const ovalCenterX = canvasWidth / 2
      const ovalCenterY = canvasHeight / 2

      const ovalRadiusX = (280 / 2) * (canvasWidth / 640)
      const ovalRadiusY = (350 / 2) * (canvasHeight / 480)

      const faceCenterX = faceBox.x + (faceBox.width / 2)
      const faceCenterY = faceBox.y + (faceBox.height / 2)

      this.debugFaceCenter = `${Math.round(faceCenterX)}, ${Math.round(faceCenterY)}`
      this.debugOvalCenter = `${Math.round(ovalCenterX)}, ${Math.round(ovalCenterY)}`

      const deltaX = faceCenterX - ovalCenterX
      const deltaY = faceCenterY - ovalCenterY

      const normalizedDistance = Math.pow(deltaX / ovalRadiusX, 2) + Math.pow(deltaY / ovalRadiusY, 2)

      return normalizedDistance <= 0.8
    },

    closeCamera() {
      this.cameraIsStarted = false
      this.picture = ''
      this.isDetecting = false
      this.faceDetected = false
      this.faceInsideOval = false
      this.showCountdown = false

      if (this.detectionInterval) {
        clearInterval(this.detectionInterval)
        this.detectionInterval = null
      }

      const videoElement = this.$refs.easyCamera?.$el?.querySelector('video')
          || this.$refs.mobileCamera?.$refs?.easyCamera?.$el?.querySelector('video')
      if (videoElement && videoElement.srcObject) {
        const tracks = videoElement.srcObject.getTracks()
        tracks.forEach(track => track.stop())
        videoElement.srcObject = null
      }

      const canvasId = this.isMobile ? 'mobile-overlay' : 'overlay'
      const canvas = document.getElementById(canvasId)
      if (canvas) {
        const ctx = canvas.getContext('2d')
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
      }

      this.$emit('camera-closed')
    },

    captureAndSend() {
      if (this.isCapturing) return
      this.isCapturing = true
      this.$loading && this.$loading(true)

      const video = this.$refs.easyCamera?.$el?.querySelector('video')
          || this.$refs.mobileCamera?.$refs?.easyCamera?.$el?.querySelector('video')
      if (!video) {
        this.isCapturing = false
        this.$message('Erro', 'Câmera não encontrada.', 'error')
        this.$loading && this.$loading(false)
        return
      }

      try {
        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = video.videoWidth
        tempCanvas.height = video.videoHeight
        const tempCtx = tempCanvas.getContext('2d')

        tempCtx.scale(-1, 1)
        tempCtx.drawImage(video, -tempCanvas.width, 0, tempCanvas.width, tempCanvas.height)

        const { videoWidth } = video
        const { videoHeight } = video
        const ovalWidth = Math.min(videoWidth * 0.6, videoHeight * 0.8) // Mantém proporção
        const ovalHeight = ovalWidth * 1.2 // Oval mais alongado
        const centerX = videoWidth / 2
        const centerY = videoHeight / 2

        const finalCanvas = document.createElement('canvas')
        finalCanvas.width = ovalWidth
        finalCanvas.height = ovalHeight
        const finalCtx = finalCanvas.getContext('2d')

        finalCtx.fillStyle = 'white'
        finalCtx.fillRect(0, 0, ovalWidth, ovalHeight)

        finalCtx.save()
        finalCtx.beginPath()
        finalCtx.ellipse(ovalWidth / 2, ovalHeight / 2, ovalWidth / 2, ovalHeight / 2, 0, 0, 2 * Math.PI)
        finalCtx.clip()

        finalCtx.drawImage(
          tempCanvas,
          centerX - ovalWidth / 2, centerY - ovalHeight / 2,
          ovalWidth, ovalHeight,
          0, 0,
          ovalWidth, ovalHeight,
        )
        finalCtx.restore()

        this.picture = finalCanvas.toDataURL('image/jpeg', 0.7)

        if (this.detectionInterval) clearInterval(this.detectionInterval)
        this.detectionInterval = null
        this.isDetecting = false

        this.handlePicture()
      } catch (error) {
        console.error('Erro na captura:', error)
        this.isCapturing = false
        this.$loading && this.$loading(false)
        this.$message('Erro', 'Falha ao capturar a foto. Tente novamente.', 'error')
      }
    },

    async startFaceDetection() {
      if (this.isDetecting) return

      console.log('Iniciando detecção facial...')
      this.isDetecting = true
      this.faceDetected = false
      this.faceInsideOval = false
      this.faceInsideCount = 0
      this.showCountdown = false
      this.captureCountdown = 0

      const video = this.$refs.easyCamera?.$el?.querySelector('video')
          || this.$refs.mobileCamera?.$refs?.easyCamera?.$el?.querySelector('video')
      if (!video) {
        console.error('Vídeo não encontrado')
        this.isDetecting = false
        return
      }

      await new Promise(resolve => {
        if (video.readyState >= 2) {
          resolve()
        } else {
          video.addEventListener('loadeddata', resolve, { once: true })
        }
      })

      const canvasId = this.isMobile ? 'mobile-overlay' : 'overlay'
      const canvas = document.getElementById(canvasId)
      if (!canvas) {
        console.error(`Canvas ${canvasId} não encontrado`)
        this.isDetecting = false
        return
      }

      const displayWidth = canvas.offsetWidth || 640
      const displayHeight = canvas.offsetHeight || 480

      canvas.width = displayWidth
      canvas.height = displayHeight

      const ctx = canvas.getContext('2d')
      console.log(`Canvas dimensions: ${displayWidth}x${displayHeight}`)

      const detectionFrequency = this.isMobile ? 100 : 300

      this.detectionInterval = setInterval(async () => {
        try {
          if (!this.cameraIsStarted || !this.isDetecting || !video || !video.readyState || video.readyState < 2) {
            return
          }

          ctx.clearRect(0, 0, canvas.width, canvas.height)

          const detections = await faceapi
            .detectAllFaces(video, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.3 }))
            .withFaceLandmarks()

          this.faceDetected = detections.length > 0
          console.log('Face detected:', detections.length > 0 ? detections[0].detection.box : 'No face')

          if (detections.length > 0 && !this.isCapturing) {
            const detection = detections[0]
            const { box } = detection.detection

            if (box) {
              const scaleX = displayWidth / video.videoWidth
              const scaleY = displayHeight / video.videoHeight

              const scaledBox = {
                x: box.x * scaleX,
                y: box.y * scaleY,
                width: box.width * scaleX,
                height: box.height * scaleY,
              }

              const isInside = this.isFaceInsideOval(scaledBox, displayWidth, displayHeight)
              this.faceInsideOval = isInside
              console.log('Face inside oval:', isInside)

              if (this.autoCapture) {
                if (isInside) {
                  this.faceInsideCount++
                  console.log('Face inside count:', this.faceInsideCount, '/', this.minFaceDetectionTime)

                  if (this.faceInsideCount >= this.minFaceDetectionTime && !this.isCapturing && !this.showCountdown) {
                    if (this.isMobile) {
                      console.log('Mobile: capturando foto automaticamente')
                      this.captureAndSend()
                    } else {
                      this.showCountdown = true
                      this.captureCountdown = 3
                      console.log('Desktop: Starting countdown for auto capture')

                      const countdownInterval = setInterval(() => {
                        this.captureCountdown--
                        console.log('Countdown:', this.captureCountdown)

                        if (this.captureCountdown <= 0) {
                          clearInterval(countdownInterval)
                          this.showCountdown = false

                          if (this.faceInsideOval && this.faceDetected && this.cameraIsStarted && !this.isCapturing) {
                            console.log('Auto capturing photo...')
                            this.captureAndSend()
                          } else {
                            console.log('Auto capture cancelled - face not in position anymore')
                          }
                        }
                      }, 1000)
                    }
                  }
                } else {
                  if (this.faceInsideCount > 0) {
                    console.log('Face left oval, resetting counter')
                    this.faceInsideCount = 0
                  }

                  if (this.showCountdown) {
                    this.showCountdown = false
                    console.log('Countdown cancelled - face left oval')
                  }
                }
              }

              ctx.strokeStyle = this.faceInsideOval ? '#00FF00' : '#FF6B00'
              ctx.lineWidth = 3
              ctx.strokeRect(scaledBox.x, scaledBox.y, scaledBox.width, scaledBox.height)

              if (this.debugInfo) {
                const centerX = scaledBox.x + scaledBox.width / 2
                const centerY = scaledBox.y + scaledBox.height / 2
                ctx.fillStyle = this.faceInsideOval ? '#00FF00' : '#FF6B00'
                ctx.beginPath()
                ctx.arc(centerX, centerY, 5, 0, 2 * Math.PI)
                ctx.fill()
              }
            }
          } else {
            this.faceInsideOval = false

            if (this.faceInsideCount > 0) {
              console.log('No face detected, resetting counter')
              this.faceInsideCount = 0
            }

            if (this.showCountdown) {
              this.showCountdown = false
              console.log('Countdown cancelled - no face detected')
            }
          }
        } catch (error) {
          console.error('Erro na detecção:', error)
        }
      }, detectionFrequency)
    },

    async loadFaceApiModels() {
      try {
        console.log('Carregando modelos Face-API...')
        const modelPath = '/models'

        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(modelPath),
          faceapi.nets.ssdMobilenetv1.loadFromUri(modelPath),
          faceapi.nets.faceLandmark68Net.loadFromUri(modelPath),
          faceapi.nets.faceRecognitionNet.loadFromUri(modelPath),
          faceapi.nets.faceExpressionNet.loadFromUri(modelPath),
        ])

        this.isLoadingModels = false
        console.log('Modelos carregados com sucesso!')
      } catch (error) {
        console.error('Erro ao carregar modelos:', error)
        this.$message('Erro', 'Falha ao carregar modelos de reconhecimento facial.', 'error')
      }
    },

    async handlePicture() {
      const img = new Image()
      img.onload = async () => {
        try {
          const detections = await faceapi.detectAllFaces(img, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.3 }))
          if (detections.length === 0) {
            this.$message('Erro', 'Nenhum rosto detectado na imagem.', 'error')
            this.isCapturing = false
            this.$loading && this.$loading(false)
            return
          }

          console.log('Rosto detectado na imagem, enviando ao servidor...')
          this.$emit('picture-captured', this.picture, this.location)
        } catch (error) {
          console.error('Erro no processamento da imagem:', error)
          this.isCapturing = false
          this.$loading && this.$loading(false)
        }
      }
      img.src = this.picture
    },
  },
}
</script>

<style scoped>
.debug-info {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px;
  border-radius: 5px;
  font-size: 12px;
  z-index: 20;
}

.camera-wrapper {
  position: relative;
  width: 100%;
  max-width: 640px;
  height: auto;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 8px;
}

.mirrored-camera {
  transform: scaleX(-1) !important;
  -webkit-transform: scaleX(-1) !important;
}

.mirrored-camera video {
  transform: scaleX(-1) !important;
  -webkit-transform: scaleX(-1) !important;
}

.overlay-canvas {
  transform: scaleX(-1) !important;
  -webkit-transform: scaleX(-1) !important;
}

.camera-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-modal-overlay {
  position: fixed;
  z-index: 1050;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 10px;
}

.custom-modal-content {
  background: #fff;
  border-radius: 8px;
  max-width: 700px;
  width: 95%;
  padding: 0;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.3);
  position: relative;
  margin: auto;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  transform: translateY(0);
}

.custom-modal-content .modal-body {
  overflow-y: auto;
  padding: 15px;
  max-height: calc(85vh - 120px);
}
</style>
